import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next/src';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import * as Yup from 'yup';
import Dialog from '@hobs/components/Dialog';
import Input from '@hobs/components/Input';
import InputLabel from '@hobs/components/InputLabel';
import {
  uploadAttachments,
  clearAttachments,
  deleteAttachment
} from '@hobs/actions/attachment';

import DropZone from '@hobs/components/DropZone';
import Toast from '@hobs/components/Toast';
import { Formik, Form, Field } from 'formik';
import { RaisedButton } from '@hobs/components/Buttons';
import Alert from '@hobs/components/Alert';
import Switch from '@hobs/components/Switch';
import {
  createInvoice,
  getForSubmit,
  clearInvoices
} from '@hobs/actions/invoices';
import Money from '@hobs/components/Money';

import './HnInvoiceCreateDialog.scss';
import Status from '@hobs/components/Status';
import { DELIVERY_LINE_STATUSES, STATUSES } from '@hobs/utils/constants';
import { Collapse, Icon } from '@blueprintjs/core';
import Quantity from '@hobs/components/Quantity';
import Checkbox from '@hobs/components/Checkbox';

const defaultState = {
  error: null,
  isPaid: false,
  collapse: {}
};

class HnInvoiceCreateDialog extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = defaultState;

    const { t } = props;
    this.validationSchema = Yup.object().shape({
      receiver: Yup.string()
        .required(t('Receiver is required'))
        .max(256, t('Receiver cannot be longer than 256 symbols'))
        .nullable()
    });
  }

  componentDidUpdate(prevProps) {
    const { getForSubmit, serviceRequestIds } = this.props;
    if (
      serviceRequestIds &&
      serviceRequestIds !== prevProps.serviceRequestIds
    ) {
      getForSubmit({ serviceRequestIds }).then(response => {
        if (!response.success && response.payload) {
          Toast.warning(response.payload.message);
        }
      });
    }
  }

  onDialogClose = () => {
    const { closeDialog, clearAttachments } = this.props;

    clearAttachments();
    closeDialog();
  };

  onDialogClosed = () => {
    const { clearInvoices } = this.props;
    clearInvoices();
    this.resetState();
  };

  setError(message) {
    this.setState({ error: message });
  }

  setIsPaid = isPaid => {
    this.setState({ isPaid });
  };

  resetState() {
    this.setState(defaultState);
  }

  handleFileUpload = value => {
    if (value) {
      this.props.uploadAttachments({ attachments: value });
    }
  };

  submitInvoice = (values, { setSubmitting, setErrors }) => {
    const {
      createInvoice,
      t,
      reload,
      serviceRequestIds,
      attachments
    } = this.props;
    const { isPaid } = this.state;
    const deliveryLineIds = values.checked
      ? Object.keys(values.checked).filter(key => values.checked[key] === true)
      : [];

    const body = {
      ...values,
      serviceRequestIds,
      isPaid,
      attachmentsIds: attachments.map(attachment => attachment.attachmentId),
      deliveryLineIds: deliveryLineIds
    };

    createInvoice(body).then(response => {
      if (response.success) {
        Toast.success(t('Invoice reference created successfully'));
        reload();
        this.onDialogClose();
      } else if (response.payload && response.payload.details) {
        const errors = response.payload.details.reduce((results, error) => {
          return {
            ...results,
            [error.target]: error.message[0]
          };
        }, {});
        if (errors) {
          setErrors(errors);
        }
      } else if (response.payload) {
        this.setError(response.payload.message);
      } else {
        setErrors(t('Cannot create invoice reference'));
      }

      setSubmitting(false);
    });
  };

  renderExerciseCurrency() {
    const { currentExercise } = this.props;

    if (!currentExercise.currency.name) {
      return;
    }

    return ` (${this.props.currentExercise.currency.name})`;
  }

  toggleCollapse = serviceRequestId => {
    this.setState(prevState => ({
      collapse: {
        ...prevState.collapse,
        [serviceRequestId]: !prevState.collapse[serviceRequestId]
      }
    }));
  };

  createDeliveryLineMap = data => {
    if (!data || !data.serviceRequests) {
      return {};
    }

    const srs = data.serviceRequests;

    return srs.reduce((acc, obj) => {
      obj.deliveryLines.forEach(line => {
        if (DELIVERY_LINE_STATUSES[line.status].type == 'Delivered') {
          acc[line.deliveryLineId] = true;
        }
      });
      return acc;
    }, {});
  };

  renderServiceRequests = (values, setFieldValue) => {
    if (!values.serviceRequests) {
      return null;
    }

    return values.serviceRequests.map(sr => {
      const isOpen = this.state.collapse[sr.serviceRequestId] || false;
      const isCancellation = sr.cancellation;
      return (
        <div className="create-invoice--requests" key={sr.serviceRequestId}>
          <div
            className={
              'create-invoice--requests--item ' +
              (!isCancellation ? 'hover-line' : '')
            }
            onClick={() => {
              if (!isCancellation) {
                this.toggleCollapse(sr.serviceRequestId);
              }
            }}
          >
            <Status name={STATUSES[sr.statusId].type} type={sr.statusId} />
            <span>{sr.requestNumber}</span>
            {isOpen ? (
              <Icon
                className="create-invoice--requests--item--arrow"
                icon="chevron-up"
              ></Icon>
            ) : !isCancellation ? (
              <Icon
                className="create-invoice--requests--item--arrow"
                icon="chevron-down"
              ></Icon>
            ) : null}
          </div>
          <Collapse isOpen={isOpen}>
            <table>
              <thead>
                <tr>
                  <th></th>
                  <th>Status</th>
                  <th>Quantity</th>
                  <th>Price</th>
                  <th>Total</th>
                  <th>Gross amount</th>
                  <th>VAT</th>
                </tr>
              </thead>
              <tbody>
                {sr.deliveryLines.map(line => {
                  return (
                    <tr
                      className={
                        DELIVERY_LINE_STATUSES[line.status].type == 'Invoiced'
                          ? 'disabled-line'
                          : ''
                      }
                    >
                      <td>
                        {DELIVERY_LINE_STATUSES[line.status].type ==
                          'Delivered' && (
                          <Field
                            name={`checked.${line.deliveryLineId}`}
                            component={Checkbox}
                            onClick={event => {
                              if (event.target.value == 'true') {
                                setFieldValue(
                                  'priceTotal',
                                  values.priceTotal - line.priceTotal
                                );
                                setFieldValue(
                                  'priceGrossAmount',
                                  values.priceGrossAmount -
                                    line.priceGrossAmount
                                );
                              } else {
                                setFieldValue(
                                  'priceTotal',
                                  values.priceTotal + line.priceTotal
                                );
                                setFieldValue(
                                  'priceGrossAmount',
                                  values.priceGrossAmount +
                                    line.priceGrossAmount
                                );
                              }
                            }}
                          />
                        )}
                      </td>
                      <td>
                        <Status
                          name={DELIVERY_LINE_STATUSES[line.status].type}
                          type={line.status}
                          statuses={DELIVERY_LINE_STATUSES}
                        />
                      </td>
                      <td>
                        <Quantity amount={line.quantity} />
                      </td>
                      <td>
                        <Money amount={line.priceUnit} />
                      </td>
                      <td>
                        <Money amount={line.priceTotal} />
                      </td>
                      <td>
                        <Money amount={line.priceGrossAmount} />
                      </td>
                      <td>
                        <span>{line.vatPercentage}%</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tr className="delivery-totals">
                <td colSpan="4">
                  <strong>Total selected</strong>
                </td>
                <td colSpan="1">
                  <strong>
                    <Money
                      amount={this.calculateDeliveryLineTotalSum(
                        values.serviceRequests,
                        'priceTotal',
                        values.checked || {}
                      )}
                      currency={this.props.currentExercise.currency.name}
                    />
                  </strong>
                </td>
                <td colSpan="2">
                  <strong>
                    <Money
                      amount={this.calculateDeliveryLineTotalSum(
                        values.serviceRequests,
                        'priceGrossAmount',
                        values.checked || {}
                      )}
                      currency={this.props.currentExercise.currency.name}
                    />
                  </strong>
                </td>
              </tr>
            </table>
          </Collapse>
        </div>
      );
    });
  };

  calculateDeliveryLineTotalSum = (data, field, checkedLines = {}) => {
    if (!data || data.length === 0) {
      return 0;
    }

    return data.reduce((acc, sr) => {
      const sum = sr.deliveryLines.reduce((lineAcc, line) => {
        if (DELIVERY_LINE_STATUSES[line.status].type == 'Delivered') {
          if (
            Object.keys(checkedLines).length === 0 ||
            checkedLines[line.deliveryLineId]
          ) {
            return lineAcc + (line[field] || 0);
          }
          return lineAcc;
        }
        return lineAcc;
      }, 0);
      return acc + sum;
    }, 0);
  };

  calculateCancellationTotalSum = (data, field) => {
    if (!data || data.length === 0) {
      return 0;
    }
    return data.reduce((acc, sr) => {
      const cancellationValue = sr.cancellation
        ? sr.cancellation[field] || 0
        : 0;
      return acc + cancellationValue;
    }, 0);
  };

  calculatePriceTotal = values => {
    if (!values || !values.serviceRequests) {
      return 0;
    }

    const deliveryLinesTotal = this.calculateDeliveryLineTotalSum(
      values.serviceRequests,
      'priceTotal'
    );
    const cancellationTotal = this.calculateCancellationTotalSum(
      values.serviceRequests,
      'totalCost'
    );

    return deliveryLinesTotal + cancellationTotal;
  };

  canculatePriceGrossAmount = values => {
    if (!values || !values.serviceRequests) {
      return 0;
    }

    const deliveryLinesTotal = this.calculateDeliveryLineTotalSum(
      values.serviceRequests,
      'priceGrossAmount'
    );

    const cancellationTotal = this.calculateCancellationTotalSum(
      values.serviceRequests,
      'grossAmount'
    );

    return deliveryLinesTotal + cancellationTotal;
  };

  render() {
    const { t, isDialogOpen, forSubmit, attachments } = this.props;
    const { error, isPaid } = this.state;

    return (
      <Dialog
        title={t('Create invoice reference')}
        isOpen={isDialogOpen && forSubmit}
        onClose={this.onDialogClose}
        onClosed={this.onDialogClosed}
        className="create-invoice"
      >
        <Formik
          validateOnBlur={false}
          validateOnChange={false}
          validationSchema={this.validationSchema}
          enableReinitialize={true}
          initialValues={{
            ...forSubmit,
            receiver: null,
            attachmentsIds: [],
            priceTotal: this.calculatePriceTotal(forSubmit),
            priceGrossAmount: this.canculatePriceGrossAmount(forSubmit),
            checked: this.createDeliveryLineMap(forSubmit)
          }}
          onSubmit={this.submitInvoice}
        >
          {({ setFieldValue, values, isSubmitting }) => (
            <Form>
              {error && <Alert type="error">{error}</Alert>}
              <div>
                <div className="create-invoice--info create-invoice--description">
                  {t(
                    'Are you sure you want to create invoice reference for the selected service lines?'
                  )}
                </div>
                {this.renderServiceRequests(values, setFieldValue)}
                <div className="create-invoice--info create-invoice--description">
                  {t('The following information will be included:')}
                </div>
                <div className="create-invoice--row">
                  <Field
                    setFieldValue={setFieldValue}
                    name="receiver"
                    placeholder="Enter the receiver"
                    component={Input}
                  >
                    {t('Receiver')}
                  </Field>
                </div>
                <div className="create-invoice--row">
                  <div className="create-invoice--column">
                    <div className="create-invoice--row">
                      <div className="create-invoice--column">
                        <InputLabel>{t('Invoiced service lines')}</InputLabel>
                        <div className="create-invoice--info">
                          {values.serviceRequestCount}
                        </div>
                      </div>
                    </div>
                    <div className="create-invoice--row">
                      {this.props.withIsPaidSwitch && (
                        <div className="create-invoice--column">
                          <InputLabel>{t('Is paid')}</InputLabel>
                          <Switch checked={isPaid} onChange={this.setIsPaid} />
                        </div>
                      )}
                    </div>
                    <div className="create-invoice--row">
                      <div className="create-invoice--column">
                        <InputLabel>
                          {t('Total actual cost')}
                          {this.renderExerciseCurrency()}
                        </InputLabel>
                        <div className="create-invoice--info">
                          <Money amount={values.priceTotal} />
                        </div>
                      </div>
                    </div>
                    <div className="create-invoice--row">
                      <div className="create-invoice--column">
                        <InputLabel>
                          {t('Total actual gross amount')}
                          {this.renderExerciseCurrency()}
                        </InputLabel>
                        <div className="create-invoice--info">
                          <Money amount={values.priceGrossAmount} />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="create-invoice--column">
                    <InputLabel>{t('Attachments')}</InputLabel>
                    <DropZone
                      multiple={true}
                      value={attachments}
                      onSelect={files => {
                        this.handleFileUpload(files);
                      }}
                      onRemove={file => {
                        this.props.deleteAttachment(file);
                      }}
                    />
                  </div>
                </div>
                <div className="create-invoice--buttons">
                  <RaisedButton type="submit" disabled={isSubmitting}>
                    {t('Create')}
                  </RaisedButton>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </Dialog>
    );
  }
}

HnInvoiceCreateDialog.defaultProps = {
  withIsPaidSwitch: true
};

HnInvoiceCreateDialog.propTypes = {
  t: PropTypes.func,
  location: PropTypes.object,
  match: PropTypes.object,
  history: PropTypes.object,
  isDialogOpen: PropTypes.bool,
  closeDialog: PropTypes.func,
  serviceRequestIds: PropTypes.array,
  createInvoice: PropTypes.func,
  forSubmit: PropTypes.object,
  getForSubmit: PropTypes.func,
  reload: PropTypes.func,
  clearInvoices: PropTypes.func,
  attachments: PropTypes.array,
  uploadAttachments: PropTypes.func,
  setAttachments: PropTypes.func,
  deleteAttachment: PropTypes.func,
  clearAttachments: PropTypes.func,
  withIsPaidSwitch: PropTypes.bool,
  currentExercise: PropTypes.object
};

const mapStateToProps = state => ({
  forSubmit: state.invoices.forSubmit,
  attachments: state.attachment.attachments,
  currentExercise: state.exercise.current
});

const mapDispatchToProps = {
  createInvoice,
  getForSubmit,
  clearInvoices,
  uploadAttachments,
  clearAttachments,
  deleteAttachment
};

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(withTranslation()(HnInvoiceCreateDialog))
);
